import { Injectable } from '@angular/core';
import { PushNotifications, Token, PermissionStatus, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Device, DeviceInfo } from '@capacitor/device';
import { App, AppInfo } from '@capacitor/app';

import { Storage } from '@capacitor/storage';
import { Network } from '@capacitor/network';
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet';
import { Dialog } from '@capacitor/dialog';

import { AngularFirestore } from '@angular/fire/firestore';
import firebase from 'firebase/app';
import 'firebase/auth';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { FirebaseCrashlytics } from '@capacitor-community/firebase-crashlytics';
import { AngularFireFunctions } from '@angular/fire/functions';

@Injectable({
  providedIn: 'root'
})
export class BootService {
    devicePlatform: "ios" | "android" | "web" = "web"
    applicationVersion: string = "1.8.6"
    liveDeployVersion: string = "0"

    isPushEnabled: boolean = true

    isIphoneX: boolean = false

    connectedToTheNetwork: boolean = true
    connectedToTheNetworkChange: Subject<boolean> = new Subject<boolean>();

    selectedLanguage: string = 'fr'

    hasNotEnteredSmsCodeYet: boolean = true

  constructor(
    public firestore: AngularFirestore,
    public fns: AngularFireFunctions,
    private translate: TranslateService
  ) {}

  // Called from app.component.ts
  async boot() {
    console.info ("--- BOOT ---")

    // Get selected language
    // this.selectedLanguage = this.translate.currentLang

    await this.getDeviceInfo()
    console.log("device platform", this.devicePlatform);

    if (this.devicePlatform !== 'web') {
        this.getApplicationVersion()
        this.initPushNotifications()
    } else
      console.log ("applicationVersion", this.applicationVersion)

    this.checkModernAppleVariants()

    // Listen to network status
    Network.addListener('networkStatusChange', status => {
      console.log('Network status changed', status);
      this.connectedToTheNetwork = status.connected
      this.connectedToTheNetworkChange.next(this.connectedToTheNetwork);
    });

    // Initialize Crashlytics
    if (this.devicePlatform !== 'web') {
      await FirebaseCrashlytics.setEnabled({enabled: true})
    }
  }

  // Get platform imformation based on Capacitor Device plugin
  async getDeviceInfo() {
    console.info ("--- getDeviceInfo ---")

    const info: DeviceInfo = await Device.getInfo();
    this.devicePlatform = info.platform
    console.log("device platform", this.devicePlatform);
  }

  // Toggle from French => English => Chineese
  toggleLanguageSelection() {
    console.info ("--- toggleLanguageSelection ---")

    if (this.selectedLanguage === 'fr') {
      this.translate.use('en')
      this.selectedLanguage = 'en'
    } else if (this.selectedLanguage === 'en') {
      this.translate.use('cn')
      this.selectedLanguage = 'cn'
    } else if (this.selectedLanguage === 'cn') {
      this.translate.use('fr')
      this.selectedLanguage = 'fr'
    }

    setTimeout(() => Storage.set({key: "NOUMO_CONSUMER_LANGUAGE", value: this.translate.currentLang}), 500)
  }

  // Get application version information based on Capacitor Device plugin
  async getApplicationVersion() {
    console.info ("--- getApplicationVersion ---")

    const info: AppInfo = await App.getInfo();
    this.applicationVersion = info.build + "." + this.liveDeployVersion
    console.log("applicationVersion", this.applicationVersion);
  }
  
  // Adapt header height to iPhone new dimensions
  checkModernAppleVariants() {
    console.info ("--- checkModernAppleVariants ---")

    let iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    console.log ("isIOS", iOS)

    let ratio = window.devicePixelRatio || 1;
    let screen = {
      width: window.screen.width * ratio,
      height: window.screen.height * ratio
    };

    // iPhone 11 Pro 1125 x 2436
    // iPhone 12 Pro 1170 x 2532
    // iPhone 11 Pro Max 1242 x 2688 
    // iPhone 12 Pro Max 1284 x 2778
    if (iOS && screen.width == 1125 && screen.height === 2436) {
      this.isIphoneX = true;
      console.log('!! iPhone X detected !!');
    } else if (iOS && screen.width == 1242 && screen.height === 2688) {
      this.isIphoneX = true;
      console.log('!! iPhone X detected !!');
    } else if (iOS && screen.width == 1170 && screen.height === 2532) {
      this.isIphoneX = true;
      console.log('!! iPhone X detected !!');
    } else if (iOS && screen.width == 1284 && screen.height === 2778) {
      this.isIphoneX = true;
      console.log('!! iPhone X detected !!');
    } else {
      this.isIphoneX = false;
      console.log('iPhone X not detected')
    }
  }

  // Request permission to use push notifications
  // iOS will prompt user and return if they granted permission or not
  // Android will just grant without prompting
  requestPushPermissions() {
    console.info ("--- requestPushPermissions ---");

    PushNotifications.requestPermissions().then((result: PermissionStatus) => {
      // 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'
      console.log ("result.receive", result.receive)

      if (result.receive === "granted") {
        // Register with Apple / Google to receive push via APNS/FCM
        PushNotifications.register();
      } else {
        // Show some error
        // alert('Error on requestPermission: ' + JSON.stringify(result.granted));
        
        console.warn('Error on requestPermission: ', result.receive);
      }
    });
  }
  
  async initPushNotifications() {
    console.info ("--- initPushNotifications ---");
    
    // Remove all badges counts
    PushNotifications.removeAllDeliveredNotifications()

    // Check permission state
    const { receive } = await PushNotifications.checkPermissions()
    console.log ("Notification permission state: ", receive)

    // 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied'
    if (receive == "denied" || receive == "prompt")
      this.isPushEnabled = false
      
    console.log ("bootService.isPushEnabled", this.isPushEnabled)

    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration',
      async (token: Token) => {
        // Do not save tokens for visitors
        if (firebase.auth().currentUser === null)
          return
          
        // alert('Push registration success, token: ' + token.value);
        console.log('Push registration success, token: ' + token.value);   
        this.isPushEnabled = true      
          
        // Register the push notification token in firestore with the new language
        // IF THE USER HAS NOT YET SUBSCRIBED TO PUSH
        // const { value } = await Storage.get({key: 'PUSH_NOTIFICATION_TOKEN'})
        // if (value === null || value != token.value) {
          // Register the language with the device
          // Does the user subscrive for EN or FR content ?
          let newDevice: any = {
            userId: firebase.auth().currentUser.uid,
            userType: "consumer",
            token: token.value,
            platform: this.devicePlatform,
            language: "fr",
            enabled: true,
            lastUpdate: new Date()
          }

          // TODO: Register the token value in firestore devices collection
          await this.firestore
          .collection('devices')
          .doc(token.value)
          .set(newDevice)

          // Register the token value in local Storage
          await Storage.set({ key: 'NOUMO_PUSH_NOTIFICATION_TOKEN', value: token.value });
        // } else {
        //   console.warn ("STORAGE PUSH_NOTIFICATION_TOKEN IS NULL OR !=")
        // }
      }
    );

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError',
      (error: any) => {
        // alert('Error on registration: ' + JSON.stringify(error));
        console.error('Error on registration: ', error);
        this.isPushEnabled = false
      }
    );

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived',
      async (notification: PushNotificationSchema) => {
        // alert('Push received: ' + JSON.stringify(notification));
        // alert('Push received: ' + JSON.stringify(notification.data));
        console.log('Push received only data: ', JSON.stringify(notification.data));
        this.handleNotificationAction(notification.data)
      }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed',
      (actionPerformed: ActionPerformed) => {
        // alert('Push action performed: ' + JSON.stringify(actionPerformed.notification));
        // alert('Push action performed: ' + JSON.stringify(actionPerformed.notification.data));
        console.log('Push tapped: ', JSON.stringify(actionPerformed.notification));
        this.handleNotificationAction(actionPerformed.notification.data)
      }
    );
  }

  async handleNotificationAction(notificationData: any) {
    console.info ("handleNotificationAction")

    if (typeof notificationData.action !== 'undefined' && notificationData.action === 'rateShop') {
      // Opens a rate shop Capacitor dialog window
      console.info ("--- showActions ---")

      const result = await ActionSheet.showActions({
        title: await this.translate.get("RATING_ACION_SHEETS.SHOP.TITLE", {shopType: notificationData.shopType, shopName: notificationData.shopName}).toPromise(),
        message: await this.translate.get("RATING_ACION_SHEETS.SHOP.MESSAGE").toPromise(),
        options: [
          {
            title: '⭐',
          },
          {
            title: '⭐⭐',
          },
          {
            title: '⭐⭐⭐',
          },
          {
            title: '⭐⭐⭐⭐',
          },
          {
            title: '⭐⭐⭐⭐⭐',
          },
          {
            title: await this.translate.get("RATING_ACION_SHEETS.SHOP.NO_THANKS").toPromise(),
            style: ActionSheetButtonStyle.Destructive,
          },
        ],
      });

      console.log('Action Sheet result:', JSON.stringify(result));

      // Update Firestore shop rating with this new score
      if (result.index < 5) {
        const rateShopCall = this.fns.httpsCallable("rateShopCall");
        rateShopCall({rating: (result.index + 1), shopType: notificationData.shopType, shopId: notificationData.shopId})
        .subscribe((results) => console.log ("rateShop results", JSON.stringify(results)))
      }

      // Thank the user for having rate the shop
      await Dialog.alert({
        title: notificationData.shopName,
        message: await this.translate.get("RATING_ACION_SHEETS.SHOP.FEEDBACK_ALERT_MESSAGE").toPromise(),
      });
    } else if (typeof notificationData.action !== 'undefined' && notificationData.action === 'rateDriver') {
      // Opens a rate shop Capacitor dialog window
      console.info ("--- showActions ---")

      const driverResult = await ActionSheet.showActions({
        title: await this.translate.get("RATING_ACION_SHEETS.DRIVER.TITLE", {driverName: notificationData.driverName}).toPromise(),
        message: await this.translate.get("RATING_ACION_SHEETS.DRIVER.MESSAGE").toPromise(),
        options: [
          {
            title: '⭐',
          },
          {
            title: '⭐⭐',
          },
          {
            title: '⭐⭐⭐',
          },
          {
            title: '⭐⭐⭐⭐',
          },
          {
            title: '⭐⭐⭐⭐⭐',
          },
          {
            title: await this.translate.get("RATING_ACION_SHEETS.DRIVER.NO_THANKS").toPromise(),
            style: ActionSheetButtonStyle.Destructive,
          },
        ],
      });

      console.log('Action Sheet driverResult:', JSON.stringify(driverResult));

      // Update Firestore shop rating with this new score
      if (driverResult.index < 5) {
        const rateDriverCall = this.fns.httpsCallable("rateDriverCall");
        rateDriverCall({rating: (driverResult.index + 1), driverId: notificationData.driverId})
        .subscribe((results) => console.log ("rateShop results", JSON.stringify(results)))
      }

      // Thank the user for having rate the shop
      await Dialog.alert({
        title: notificationData.driverName,
        message: await this.translate.get("RATING_ACION_SHEETS.DRIVER.FEEDBACK_ALERT_MESSAGE").toPromise(),
      });
    } else if (typeof notificationData.action !== 'undefined' && notificationData.action === 'rateShopAndDriver') {
      // Opens a rate shop Capacitor dialog window
      console.info ("--- showActions ---")

      const shopResult = await ActionSheet.showActions({
        title: await this.translate.get("RATING_ACION_SHEETS.SHOP.TITLE", {shopType: notificationData.shopType, shopName: notificationData.shopName}).toPromise(),
        message: await this.translate.get("RATING_ACION_SHEETS.SHOP.MESSAGE").toPromise(),
        options: [
          {
            title: '⭐',
          },
          {
            title: '⭐⭐',
          },
          {
            title: '⭐⭐⭐',
          },
          {
            title: '⭐⭐⭐⭐',
          },
          {
            title: '⭐⭐⭐⭐⭐',
          },
          {
            title: await this.translate.get("RATING_ACION_SHEETS.SHOP.NO_THANKS").toPromise(),
            style: ActionSheetButtonStyle.Destructive,
          },
        ],
      });

      console.log('Action Sheet shopResult:', JSON.stringify(shopResult));

      // Update Firestore shop rating with this new score
      const rateShopCall = this.fns.httpsCallable("rateShopCall");
      rateShopCall({rating: (shopResult.index + 1), shopType: notificationData.shopType, shopId: notificationData.shopId})
      .subscribe((results) => console.log ("rateShop results", JSON.stringify(results)))

      const driverResult = await ActionSheet.showActions({
        title: await this.translate.get("RATING_ACION_SHEETS.DRIVER.TITLE", {driverName: notificationData.driverName}).toPromise(),
        message: await this.translate.get("RATING_ACION_SHEETS.DRIVER.MESSAGE").toPromise(),
        options: [
          {
            title: '⭐',
          },
          {
            title: '⭐⭐',
          },
          {
            title: '⭐⭐⭐',
          },
          {
            title: '⭐⭐⭐⭐',
          },
          {
            title: '⭐⭐⭐⭐⭐',
          },
          {
            title: await this.translate.get("RATING_ACION_SHEETS.DRIVER.NO_THANKS").toPromise(),
            style: ActionSheetButtonStyle.Destructive,
          },
        ],
      });

      console.log('Action Sheet driverResult:', JSON.stringify(driverResult));

      // Update Firestore shop rating with this new score
      const rateDriverCall = this.fns.httpsCallable("rateDriverCall");
      rateDriverCall({rating: (driverResult.index + 1), driverId: notificationData.driverId})
      .subscribe((results) => console.log ("rateShop results", JSON.stringify(results)))

      // Thank the user for having rate the shop
      await Dialog.alert({
        title: await this.translate.get("RATING_ACION_SHEETS.DRIVER.FEEDBACK_ALERT_TITLE").toPromise(),
        message: await this.translate.get("RATING_ACION_SHEETS.DRIVER.FEEDBACK_ALERT_MESSAGE").toPromise(),
      });
    }
  }
}